import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* Every amount on /admin/orders is an integer number of PAISE. */
const rupees = (paise) => (Number(paise) || 0) / 100;

/* Window bounds are calendar days on the STORE's clock (Asia/Kolkata); the
   service resolves the boundaries, we only name the days. */
const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

/* There is no "all time" window: /admin/orders/summary with neither bound
   means TODAY. A `from` with no `to` leaves the upper end open, so an early
   day is how everything-so-far is asked for. */
const ALL_TIME_FROM = "2000-01-01";

/* One range, two aggregates — the money from /summary and the per-status
   breakdown from /status-counts, both grouped in SQL under the same store
   scope and the same visibility filter (an abandoned gateway checkout is not
   an order). This used to pull the order list into the browser and add rupee
   floats over it, which now returns one page of ten. */
const loadRange = async ({ from, to } = {}) => {
  const bounds = {};
  if (from) bounds.from = from;
  if (to) bounds.to = to;

  const summaryQs = new URLSearchParams(bounds).toString();
  /* /summary defaults to today when it is given no bounds; /status-counts
     defaults to every order ever, and needs range=today to agree with it. */
  const countsQs = new URLSearchParams(
    from || to ? bounds : { range: "today" }
  ).toString();

  const [summaryRes, countsRes] = await Promise.all([
    api.get(`/admin/orders/summary${summaryQs ? `?${summaryQs}` : ""}`),
    api.get(`/admin/orders/status-counts?${countsQs}`),
  ]);

  const s = summaryRes.data || {};
  const counts = countsRes.data?.counts || {};
  const n = (...statuses) =>
    statuses.reduce((sum, k) => sum + (Number(counts[k]) || 0), 0);

  return {
    revenue: rupees(s.revenuePaise),
    /* The service has no notion of profit. This keeps the figure the
       dashboard has always shown — turnover less what was discounted off it
       — rather than inventing a margin the ledger cannot support. */
    profit: rupees((Number(s.revenuePaise) || 0) - (Number(s.discountPaise) || 0)),
    ordersReceived: Number(s.orders) || 0,
    ordersCancelled: Number(s.cancelled) || 0,
    /* Statuses, not the old numeric orderStatus codes; the legacy spellings
       ride along because old rows still carry them. */
    ordersAccepted: n("accepted", "assigned"),
    ordersOnTheWay: n("out_for_delivery", "out_delivery", "shipped"),
    ordersDelivered: n("delivered", "left_at_doorstep", "completed"),
  };
};

/**
 * =========================
 * FETCH DASHBOARD STATS
 * =========================
 */
export const fetchDashboardStats = createAsyncThunk(
  "dashboard/fetchDashboardStats",
  async (_, { rejectWithValue }) => {
    try {
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const [today, thisMonth, total] = await Promise.all([
        loadRange(),
        loadRange({ from: ymd(firstDayOfMonth), to: ymd(now) }),
        loadRange({ from: ALL_TIME_FROM }),
      ]);

      return { today, thisMonth, total };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.code ||
          "Failed to fetch dashboard stats"
      );
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    todayStats: {
      revenue: 0,
      profit: 0,
      ordersReceived: 0,
      ordersCancelled: 0,
      ordersAccepted: 0,
      ordersOnTheWay: 0,
      ordersDelivered: 0,
    },
    thisMonthStats: {
      revenue: 0,
      profit: 0,
      ordersReceived: 0,
      ordersCancelled: 0,
      ordersAccepted: 0,
      ordersOnTheWay: 0,
      ordersDelivered: 0,
    },
    totalStats: {
      revenue: 0,
      profit: 0,
      ordersReceived: 0,
      ordersCancelled: 0,
      ordersDelivered: 0,
    },
    loading: false,
    error: null,
  },
  reducers: {
    clearDashboardError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.todayStats = action.payload.today;
        state.thisMonthStats = action.payload.thisMonth;
        state.totalStats = action.payload.total;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearDashboardError } = dashboardSlice.actions;
export default dashboardSlice.reducer;